import { InfiniteData, QueryKey } from '@tanstack/query-core';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { CountryCode } from 'common/api-types/common';
import {
  DelistingRecommendationsListRequest,
  DelistingRecommendationsResponse,
  ListingRecommendationsListRequest,
  ListingRecommendationsResponse,
  PerformanceOverviewRequest,
  PerformanceOverviewResponse,
  PromotionRecommendationsListRequest,
  PromotionRecommendationsResponse,
  RecommendationPreferencesResponse,
} from 'common/api-types/recommendations';
import {
  AxiosDelistingRecommendationsError,
  AxiosPerformanceOverviewError,
  AxiosPromotionRecommendationsError,
} from 'common/types';

import {
  fetchListingRecommendations,
  fetchPerformanceOverview,
  fetchPerformanceOverviewPreferences,
  fetchPromotionRecommendations,
  fetchDelistingRecommendations,
} from './api';

const SELECTION_RECOMMENDATION_PRFERENCES = 'selectionRecommendationPreferences';
const SELECTION_RECOMMENDATION_PERFORMANCE_OVERVIEW = 'selectionRecommendationPerformanceOverview';
export const LISTING_RECOMMENDATIONS_QUERY_KEY_ROOT = 'listingRecommendations';
export const DELISTING_RECOMMENDATIONS_QUERY_KEY_ROOT = 'delistingRecommendations';
export const PROMOTION_RECOMMENDATIONS_QUERY_KEY_ROOT = 'promotionRecommendations';

export const usePerformanceOverviewPreferences = (countryCode: CountryCode, enabled: boolean) => {
  return useQuery<RecommendationPreferencesResponse, AxiosPerformanceOverviewError>({
    queryKey: [SELECTION_RECOMMENDATION_PRFERENCES, countryCode],
    queryFn: () => fetchPerformanceOverviewPreferences(countryCode),
    enabled,
  });
};

export const usePerformanceOverview = (
  countryCode: CountryCode,
  request: PerformanceOverviewRequest,
) => {
  return useQuery<PerformanceOverviewResponse, AxiosPerformanceOverviewError>({
    queryKey: [SELECTION_RECOMMENDATION_PERFORMANCE_OVERVIEW, countryCode, request],
    queryFn: () => fetchPerformanceOverview(countryCode, request),
    refetchOnWindowFocus: false,
  });
};

export function useListingRecommendationsInfiniteQuery(
  countryCode: CountryCode,
  request: ListingRecommendationsListRequest,
) {
  const queryState = useInfiniteQuery<
    ListingRecommendationsResponse,
    AxiosDelistingRecommendationsError,
    InfiniteData<ListingRecommendationsResponse>,
    QueryKey,
    string | undefined
  >({
    queryKey: [LISTING_RECOMMENDATIONS_QUERY_KEY_ROOT, countryCode, request],
    queryFn: ({ pageParam }) => {
      if (pageParam) {
        request['nextPageToken'] = pageParam;
      }
      return fetchListingRecommendations(countryCode, request);
    },
    refetchOnWindowFocus: false,
    getNextPageParam: ({ meta }) => meta.nextPageToken,
    initialPageParam: undefined,
    staleTime: Infinity,
  });

  const listingRecommendations =
    queryState.data?.pages.flatMap((page) =>
      page.items.map((item) => ({ ...item, id: item.id })),
    ) ?? [];

  return {
    ...queryState,
    totalCount: queryState.data?.pages[0].meta.totalCount ?? 0,
    listingRecommendations,
  };
}

export function usePromotionRecommendationsInfiniteQuery(
  countryCode: CountryCode,
  request: PromotionRecommendationsListRequest,
) {
  const queryState = useInfiniteQuery<
    PromotionRecommendationsResponse,
    AxiosPromotionRecommendationsError,
    InfiniteData<PromotionRecommendationsResponse>,
    QueryKey,
    string | undefined
  >({
    queryKey: [PROMOTION_RECOMMENDATIONS_QUERY_KEY_ROOT, countryCode, request],
    queryFn: ({ pageParam }) => {
      if (pageParam) {
        request['nextPageToken'] = pageParam;
      }
      return fetchPromotionRecommendations(countryCode, request);
    },
    refetchOnWindowFocus: false,
    getNextPageParam: ({ meta }) => meta.nextPageToken,
    initialPageParam: undefined,
    staleTime: Infinity,
  });

  const promotionRecommendations =
    queryState.data?.pages.flatMap((page) =>
      page.items.map((item) => ({ ...item, id: item.product.id, subRows: item.venueItems })),
    ) ?? [];

  return {
    ...queryState,
    totalCount: queryState.data?.pages[0].meta.totalCount ?? 0,
    promotionRecommendations,
  };
}

export function useDelistingRecommendationsInfiniteQuery(
  countryCode: CountryCode,
  request: DelistingRecommendationsListRequest,
) {
  const queryState = useInfiniteQuery<
    DelistingRecommendationsResponse,
    AxiosDelistingRecommendationsError,
    InfiniteData<DelistingRecommendationsResponse>,
    QueryKey,
    string | undefined
  >({
    queryKey: [DELISTING_RECOMMENDATIONS_QUERY_KEY_ROOT, countryCode, request],
    queryFn: ({ pageParam }) => {
      if (pageParam) {
        request['nextPageToken'] = pageParam;
      }
      return fetchDelistingRecommendations(countryCode, request);
    },
    refetchOnWindowFocus: false,
    getNextPageParam: ({ meta }) => meta.nextPageToken,
    initialPageParam: undefined,
    staleTime: Infinity,
  });

  const delistingRecommendations =
    queryState.data?.pages.flatMap((page) =>
      page.items.map((item) => ({ ...item, id: item.product.id, subRows: item.venueItems })),
    ) ?? [];

  return {
    ...queryState,
    totalCount: queryState.data?.pages[0].meta.totalCount ?? 0,
    delistingRecommendations,
  };
}
